var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styled, { withTheme } from 'styled-components';
import { rem } from '../utils/Polished';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import SvgCheck from '../Icon/SvgCheck';
import SvgMinus from '../Icon/SvgMinus';
import Box from '../Box';
import Typography from '../Typography';
const StyledFakeCheck = styled.div `
  flex-shrink: 0;
  display: block;
  box-sizing: border-box;
  height: ${(props) => props.theme.SizeHeightCheckboxBase};
  width: ${(props) => props.theme.SizeWidthCheckboxBase};
  overflow: hidden;
  border-width: ${(props) => props.theme.SizeBorderWidthCheckboxBase};
  border-style: solid;
  border-color: ${(props) => props.theme.ColorBorderCheckboxBase};
  border-radius: ${(props) => props.theme.SizeBorderRadiusCheckboxBase};
  background-color: transparent;

  & > svg {
    margin-top: ${rem('-3.5px')};
    margin-left: ${rem('-3px')};
  }

  ._ndsCheck--checked & {
    border-color: ${(props) => props.theme.ColorBorderCheckboxChecked};
    background-color: ${(props) => props.theme.ColorBackgroundCheckboxChecked};
  }

  ._ndsCheck--checked._ndsCheck--error & {
    border-color: ${(props) => props.theme.ColorBorderCheckboxError};
    background-color: ${(props) => props.theme.ColorBackgroundCheckboxError};
  }

  ._ndsCheck--error & {
    border-color: ${(props) => props.theme.ColorBorderCheckboxError};
    background-color: transparent;
  }

  ._ndsCheck--checked._ndsCheck--disabled & {
    border-color: ${(props) => props.theme.ColorBorderCheckboxDisabled};
    background-color: ${(props) => props.theme.ColorBackgroundCheckboxCheckedDisabled};
  }

  ._ndsCheck--disabled & {
    border-color: ${(props) => props.theme.ColorBorderCheckboxDisabled};
    background-color: transparent;
  }

  will-change: border-color, background-color, box-shadow;
  transition: border-color 0.3s, background-color 0.3s, box-shadow 0.3s;
`;
const RealHiddenCheck = styled.input.attrs((props) => (Object.assign({ type: 'checkbox' }, props))) `
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
const WrapperLabel = styled.label `
  user-select: none;

  display: inline-flex;
  align-items: center;
  height: ${rem('20px')};

  & > span {
    will-change: color;
    transition: color 0.3s;
  }

  &._ndsCheck--disabled > span {
    color: ${(props) => props.theme.ColorFontCheckboxDisabled};
  }

  :hover {
    cursor: pointer;
  }

  &._ndsCheck--disabled:hover {
    cursor: not-allowed;
  }

  ._ndsCheck__styled {
    position: relative;
    > svg {
      position: absolute;
    }
  }

  & input:focus + ._ndsCheck__styled,
  :hover ._ndsCheck__styled {
    border-color: ${(props) => props.theme.ColorBorderCheckboxHover};
    box-shadow: ${(props) => props.theme.SizeShadowCheckbox}
      ${(props) => props.theme.ColorShadowCheckboxHover};
    background-color: transparent;
  }

  &._ndsCheck--checked input:focus + ._ndsCheck__styled,
  &._ndsCheck--checked:hover ._ndsCheck__styled {
    border-color: ${(props) => props.theme.ColorBorderCheckboxCheckedHover};
    background-color: ${(props) => props.theme.ColorBackgroundCheckboxCheckedHover};
  }

  &._ndsCheck--error input:focus + ._ndsCheck__styled,
  &._ndsCheck--error:hover ._ndsCheck__styled {
    border-color: ${(props) => props.theme.ColorBorderCheckboxErrorHover};
    box-shadow: ${(props) => props.theme.SizeShadowCheckbox}
      ${(props) => props.theme.ColorShadowCheckboxErrorHover};
  }

  &._ndsCheck--checked._ndsCheck--error input:focus + ._ndsCheck__styled,
  &._ndsCheck--checked._ndsCheck--error:hover ._ndsCheck__styled {
    background-color: ${(props) => props.theme.ColorBackgroundCheckboxErrorHover};
  }

  &._ndsCheck--checked._ndsCheck--disabled input:focus + ._ndsCheck__styled,
  &._ndsCheck--checked._ndsCheck--disabled:hover ._ndsCheck__styled {
    border-color: ${(props) => props.theme.ColorBorderCheckboxDisabled};
    background-color: ${(props) => props.theme.ColorBackgroundCheckboxCheckedDisabled};
  }

  &._ndsCheck--disabled:hover ._ndsCheck__styled {
    border-color: ${(props) => props.theme.ColorBorderCheckboxDisabledHover};
    box-shadow: none;
  }
`;
const Checkbox = (props) => {
    const { disabled, checked: checkedProp, error, indeterminate, label, onChange, forwardedRef, className, id, name, value, theme } = props, rest = __rest(props, ["disabled", "checked", "error", "indeterminate", "label", "onChange", "forwardedRef", "className", "id", "name", "value", "theme"]);
    const [checkedState, setCheckedState] = React.useState(Boolean(checkedProp));
    const { current: isControlled } = React.useRef(checkedProp !== undefined);
    const handleCheckboxChange = React.useCallback((event) => {
        const { target } = event;
        if (!isControlled && !disabled) {
            setCheckedState(target.checked);
        }
        if (onChange && !disabled) {
            onChange(event, target.checked);
        }
    }, [onChange, disabled, isControlled]);
    const handleKeyPress = React.useCallback((event) => {
        event.target.click();
    }, []);
    const checked = isControlled ? checkedProp : checkedState;
    let renderIcon = null;
    if (checked) {
        const IconComp = indeterminate ? SvgMinus : SvgCheck;
        renderIcon = React.createElement(IconComp, { color: theme.ColorIconCheckboxBase, size: "m" });
    }
    const labelComp = React.useMemo(() => {
        if (!label) {
            return null;
        }
        if (typeof label === 'string') {
            return (React.createElement(Box, { ml: 0.75 },
                React.createElement(Typography, { variant: "body3" }, label)));
        }
        return React.createElement(Box, { ml: 0.75 }, label);
    }, [label]);
    return (React.createElement(WrapperLabel, { onKeyPress: handleKeyPress, className: clsx({ '_ndsCheck--checked': checked }, { '_ndsCheck--error': error }, { '_ndsCheck--disabled': disabled }, className) },
        React.createElement(RealHiddenCheck, Object.assign({ ref: forwardedRef, checked: checked, onChange: handleCheckboxChange, id: id, name: name, value: value }, rest)),
        React.createElement(StyledFakeCheck, { className: "_ndsCheck__styled" }, renderIcon),
        labelComp));
};
Checkbox.propTypes = {
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    indeterminate: PropTypes.bool,
    label: PropTypes.node,
    onChange: PropTypes.func,
    forwardedRef: PropTypes.any,
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    theme: PropTypes.any
};
Checkbox.defaultProps = {
    checked: undefined,
    disabled: false,
    indeterminate: false,
    label: null,
    onChange: null,
    forwardedRef: null,
    id: undefined,
    name: undefined,
    value: undefined,
    className: undefined,
    theme: {}
};
const CheckBoxWithTheme = withTheme(Checkbox);
export { Checkbox as CheckboxRootComp };
export default withForwardedRef(CheckBoxWithTheme);
//# sourceMappingURL=Checkbox.js.map